import { useState, useEffect, useCallback } from 'react'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { MaskedInput } from '../../components/ui/MaskedInput'
import { Modal } from '../../components/ui/Modal'
import { Plus, Search, Edit2, Trash2, Check, X, Package, ChevronLeft, ChevronRight } from 'lucide-react'
import { industriaService } from '../../services/industriaService'
import { produtoService } from '../../services/produtoService'
import type { Industria } from '../../types'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from '../../utils/toast'
import { TableSkeleton } from '../../components/ui/Skeleton'
import { validateCNPJ, validateEmail, validatePhone, validateCEP } from '../../utils/validators'
import { buscarCEP, buscarCNPJ } from '../../services/api'

const industriaSchema = z.object({
  razaoSocial: z.string().min(3, 'Razão Social deve ter no mínimo 3 caracteres'),
  nomeFantasia: z.string().min(3, 'Nome Fantasia deve ter no mínimo 3 caracteres'),
  cnpj: z.string().refine((cnpj) => validateCNPJ(cnpj), { message: 'CNPJ inválido' }),
  email: z.string().refine((email) => validateEmail(email), { message: 'E-mail inválido' }),
  telefone: z.string().refine((phone) => validatePhone(phone), { message: 'Telefone inválido' }),
  cep: z.string().refine((cep) => validateCEP(cep), { message: 'CEP inválido' }),
  logradouro: z.string().min(3, 'Logradouro deve ter no mínimo 3 caracteres'),
  numero: z.string().min(1, 'Número é obrigatório'),
  complemento: z.string().optional(),
  bairro: z.string().min(2, 'Bairro deve ter no mínimo 2 caracteres'),
  cidade: z.string().min(2, 'Cidade deve ter no mínimo 2 caracteres'),
  estado: z.string().length(2, 'Estado deve ter 2 caracteres'),
  ativo: z.boolean(),
})

type IndustriaFormData = z.infer<typeof industriaSchema>

export function IndustriasPage() {
  const [industrias, setIndustrias] = useState<Industria[]>([])
  const [produtoCount, setProdutoCount] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingIndustria, setEditingIndustria] = useState<Industria | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativo' | 'inativo'>('all')
  const [loadingCEP, setLoadingCEP] = useState(false)
  const [loadingCNPJ, setLoadingCNPJ] = useState(false)
  
  // Paginação controlada pela API
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IndustriaFormData>({
    resolver: zodResolver(industriaSchema),
    defaultValues: { ativo: true },
  })

  const cepValue = watch('cep')
  const cnpjValue = watch('cnpj')

  const loadIndustrias = useCallback(async () => {
    setLoading(true)
    try {
      const result = await industriaService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: 'razaoSocial:asc'
      })

      setIndustrias(result.data)
      setTotalPages(result.pagination.totalPages)
      setTotalItems(result.pagination.total)
      
      // Contar produtos por indústria
      const counts: Record<string, number> = {}
      for (const industria of result.data) {
        try {
          const produtosResult = await produtoService.getAll({
            industriaId: industria.id,
            limit: 1 // Só precisamos do total
          })
          counts[industria.id] = produtosResult.pagination.total
        } catch {
          counts[industria.id] = 0
        }
      }
      setProdutoCount(counts)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar indústrias'
      toast.error(message)
      console.error('Erro ao carregar indústrias:', error)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }, [currentPage, itemsPerPage, searchTerm, statusFilter])

  useEffect(() => {
    if (isInitialLoad) {
      loadIndustrias()
    }
  }, [isInitialLoad, loadIndustrias])

  // Recarrega quando filtros/paginação mudam (após load inicial)
  useEffect(() => {
    if (!isInitialLoad) {
      loadIndustrias()
    }
  }, [currentPage, statusFilter])

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1)
      } else {
        loadIndustrias()
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, currentPage, loadIndustrias])

  const handleCEPBlur = async () => {
    if (!cepValue || cepValue.replace(/\D/g, '').length !== 8) return
    
    setLoadingCEP(true)
    const data = await buscarCEP(cepValue)
    setLoadingCEP(false)

    if (data) {
      setValue('logradouro', data.logradouro)
      setValue('bairro', data.bairro)
      setValue('cidade', data.localidade)
      setValue('estado', data.uf)
      toast.success('Endereço encontrado!')
    } else {
      toast.error('CEP não encontrado')
    }
  }

  const handleCNPJBlur = async () => {
    if (!cnpjValue || cnpjValue.replace(/\D/g, '').length !== 14) return
    
    setLoadingCNPJ(true)
    const data = await buscarCNPJ(cnpjValue)
    setLoadingCNPJ(false)

    if (data) {
      setValue('razaoSocial', data.nome)
      setValue('nomeFantasia', data.fantasia || data.nome)
      
      // Formatar telefone se vier no formato DDD+número
      const telefone = data.telefone || ''
      if (telefone && telefone.length >= 10) {
        const cleaned = telefone.replace(/\D/g, '')
        setValue('telefone', cleaned)
      }
      
      setValue('email', data.email || '')
      setValue('cep', data.cep?.replace(/\D/g, '') || '')
      setValue('logradouro', data.logradouro || '')
      setValue('numero', data.numero || '')
      setValue('complemento', data.complemento || '')
      setValue('bairro', data.bairro || '')
      setValue('cidade', data.municipio || '')
      setValue('estado', data.uf || '')
      
      toast.success('Dados da empresa encontrados!')
      
      // Se o CEP foi preenchido, buscar o endereço
      if (data.cep) {
        const cepData = await buscarCEP(data.cep)
        if (cepData) {
          setValue('logradouro', cepData.logradouro)
          setValue('bairro', cepData.bairro)
          setValue('cidade', cepData.localidade)
          setValue('estado', cepData.uf)
        }
      }
    } else {
      toast.error('CNPJ não encontrado ou inválido')
    }
  }

  const openModal = (industria?: Industria) => {
    if (industria) {
      setEditingIndustria(industria)
      reset({
        razaoSocial: industria.razaoSocial,
        nomeFantasia: industria.nomeFantasia,
        cnpj: industria.cnpj,
        email: industria.email,
        telefone: industria.telefone,
        cep: industria.cep,
        logradouro: industria.logradouro,
        numero: industria.numero,
        complemento: industria.complemento,
        bairro: industria.bairro,
        cidade: industria.cidade,
        estado: industria.estado,
        ativo: industria.ativo,
      })
    } else {
      setEditingIndustria(null)
      reset({
        razaoSocial: '',
        nomeFantasia: '',
        cnpj: '',
        email: '',
        telefone: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        ativo: true,
      })
    }
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingIndustria(null)
    reset()
  }

  const onSubmit = async (data: IndustriaFormData) => {
    try {
      const payload = {
        razaoSocial: data.razaoSocial,
        nomeFantasia: data.nomeFantasia,
        cnpj: data.cnpj.replace(/\D/g, ''),
        email: data.email,
        telefone: data.telefone.replace(/\D/g, ''),
        cep: data.cep.replace(/\D/g, ''),
        logradouro: data.logradouro,
        numero: data.numero,
        complemento: data.complemento,
        bairro: data.bairro,
        cidade: data.cidade,
        estado: data.estado.toUpperCase(),
        ativo: data.ativo,
      }

      if (editingIndustria) {
        await industriaService.update(editingIndustria.id, payload)
        toast.success('Indústria atualizada com sucesso!')
      } else {
        await industriaService.create(payload)
        toast.success('Indústria criada com sucesso!')
      }

      await loadIndustrias()
      closeModal()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar indústria'
      toast.error(message)
      console.error('Erro ao salvar indústria:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta indústria?')) {
      try {
        await industriaService.delete(id)
        await loadIndustrias()
        toast.success('Indústria excluída com sucesso!')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao excluir indústria'
        toast.error(message)
        console.error('Erro ao excluir indústria:', error)
      }
    }
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Indústrias
          </h1>
          <Button onClick={() => openModal()}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Indústria
          </Button>
        </div>

        <Card>
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por nome, CNPJ ou e-mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setStatusFilter('ativo')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'ativo'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Ativos
              </button>
              <button
                onClick={() => setStatusFilter('inativo')}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${
                  statusFilter === 'inativo'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Inativos
              </button>
            </div>
          </div>

          {loading && isInitialLoad ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto animate-fadeIn" style={{ minHeight: '200px' }}>
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Razão Social
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Nome Fantasia
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        CNPJ
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Cidade/Estado
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Produtos
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Status
                      </th>
                      <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {industrias.map((industria) => (
                      <tr key={industria.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {industria.razaoSocial}
                        </td>
                        <td className="py-4 text-sm text-gray-500 dark:text-gray-400">
                          {industria.nomeFantasia}
                        </td>
                        <td className="py-4 text-sm text-gray-500 dark:text-gray-400">
                          {industria.cnpj}
                        </td>
                        <td className="py-4 text-sm text-gray-500 dark:text-gray-400">
                          {industria.cidade}/{industria.estado}
                        </td>
                        <td className="py-4">
                          <span className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <Package className="h-4 w-4" />
                            {produtoCount[industria.id] || 0}
                          </span>
                        </td>
                        <td className="py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                              industria.ativo
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {industria.ativo ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            {industria.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openModal(industria)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(industria.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {industrias.length === 0 && (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Nenhuma indústria encontrada</p>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4 animate-fadeIn">
                {industrias.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Nenhuma indústria encontrada</p>
                  </div>
                ) : (
                  industrias.map((industria) => (
                    <div
                      key={industria.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {industria.razaoSocial}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {industria.nomeFantasia}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                            industria.ativo
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {industria.ativo ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          {industria.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">CNPJ:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {industria.cnpj}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Cidade:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {industria.cidade}/{industria.estado}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Produtos:</span>
                          <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                            <Package className="h-4 w-4" />
                            {produtoCount[industria.id] || 0}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openModal(industria)}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(industria.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Paginação */}
          {!loading && totalItems > 0 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrar
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  de {totalItems} registros
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Página {currentPage} de {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingIndustria ? 'Editar Indústria' : 'Nova Indústria'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* CNPJ */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              CNPJ * {loadingCNPJ && <span className="text-blue-600 text-xs ml-2">Buscando dados do CNPJ...</span>}
            </label>
            <Controller
              name="cnpj"
              control={control}
              render={({ field }) => (
                <MaskedInput
                  mask="cnpj"
                  placeholder="00.000.000/0000-00"
                  error={errors.cnpj?.message}
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={handleCNPJBlur}
                />
              )}
            />
          </div>

          {/* Razão Social */}
          <Input
            label="Razão Social *"
            placeholder="Nome da empresa"
            error={errors.razaoSocial?.message}
            {...register('razaoSocial')}
          />

          {/* Nome Fantasia */}
          <Input
            label="Nome Fantasia *"
            placeholder="Nome de mercado"
            error={errors.nomeFantasia?.message}
            {...register('nomeFantasia')}
          />

          {/* E-mail */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              E-mail *
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <MaskedInput
                  mask="email"
                  placeholder="contato@empresa.com.br"
                  error={errors.email?.message}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Telefone *
            </label>
            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <MaskedInput
                  mask="phone"
                  placeholder="(00) 00000-0000"
                  error={errors.telefone?.message}
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Endereço */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Endereço
            </h3>

            {/* CEP */}
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                CEP * {loadingCEP && <span className="text-blue-600 text-xs ml-2">Buscando CEP...</span>}
              </label>
              <Controller
                name="cep"
                control={control}
                render={({ field }) => (
                  <MaskedInput
                    mask="cep"
                    placeholder="00000-000"
                    error={errors.cep?.message}
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={handleCEPBlur}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Logradouro */}
              <div className="sm:col-span-2">
                <Input
                  label="Logradouro *"
                  placeholder="Rua, Avenida, etc."
                  error={errors.logradouro?.message}
                  {...register('logradouro')}
                />
              </div>

              {/* Número */}
              <Input
                label="Número *"
                placeholder="0000"
                error={errors.numero?.message}
                {...register('numero')}
              />

              {/* Complemento */}
              <Input
                label="Complemento"
                placeholder="Sala, Andar, etc."
                error={errors.complemento?.message}
                {...register('complemento')}
              />

              {/* Bairro */}
              <Input
                label="Bairro *"
                placeholder="Nome do bairro"
                error={errors.bairro?.message}
                {...register('bairro')}
              />

              {/* Cidade */}
              <Input
                label="Cidade *"
                placeholder="Nome da cidade"
                error={errors.cidade?.message}
                {...register('cidade')}
              />

              {/* Estado */}
              <div className="sm:col-span-2">
                <Input
                  label="Estado * (UF)"
                  placeholder="SP"
                  maxLength={2}
                  error={errors.estado?.message}
                  {...register('estado')}
                />
              </div>
            </div>
          </div>

          {/* Ativo */}
          <div className="flex items-center gap-2 pt-4">
            <input
              type="checkbox"
              id="ativo"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              {...register('ativo')}
            />
            <label htmlFor="ativo" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Indústria ativa
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="ghost" onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingIndustria ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </MainLayout>
  )
}
